import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Download, FileText, MapPin, User, Calendar, Loader2, AlertCircle, Shield } from "lucide-react";
import { LOGO_URL } from "@/config/branding";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const CERT_LABELS = {
  domestic_eic: "Domestic EIC", minor_works: "Minor Works", three_circuit_mw: "3 Circuit Minor Works",
  mcs_solar_pv_install: "MCS Solar PV Install", pat_testing: "PAT Testing Report",
  gas_safety_record: "Gas Safety Record", gas_boiler_commissioning: "Gas Boiler Commissioning",
  gas_boiler_service: "Boiler Service Report",
};

export default function CustomerPortalPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/certificate/${token}`)
      .then((res) => setData(res.data))
      .catch(() => setError("Certificate not found or link has expired."))
      .finally(() => setLoading(false));
  }, [token]);

  const handleDownload = () => {
    window.open(`${API}/public/certificate/${token}/download`, "_blank");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F5F5F7' }}>
        <Loader2 className="w-8 h-8 animate-spin text-[#00A3E0]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#F5F5F7' }}>
        <nav className="bg-white border-b border-[#E5E5EA] py-3 px-6">
          <Link to="/"><img src={LOGO_URL} alt="Self Certify UK" className="h-12 object-contain" /></Link>
        </nav>
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="bg-white rounded-2xl p-10 text-center max-w-md" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-[#EF4444]" />
            <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif', color: '#1C1C1E' }}>Certificate Not Found</h2>
            <p className="text-sm" style={{ color: '#8E8E93' }}>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  const cert = data.certificate;
  const source = data.source;
  const certType = CERT_LABELS[(cert.type || "").replace(/-/g, "_")] || cert.type || "Certificate";
  const certId = cert.certificate_id || cert.id || "";
  const address = source === "notification"
    ? [cert.premise, cert.street, cert.town, cert.postcode].filter(Boolean).join(", ")
    : cert.installation_address || "";
  const client = cert.client_name || cert.homeowner_name || "";
  const date = cert.cert_date || cert.installation_date || cert.created_at?.slice(0, 10) || "";
  const contractor = cert.contractor_trading_title || cert.technician_company || "";

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#F5F5F7' }} data-testid="customer-portal-page">
      {/* Nav */}
      <nav className="bg-white/95 backdrop-blur-md border-b border-[#E5E5EA] sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/"><img src={LOGO_URL} alt="Self Certify UK" className="h-12 object-contain" /></Link>
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-[#22C55E]" />
            <span className="text-xs font-semibold" style={{ color: '#22C55E' }}>Verified Certificate</span>
          </div>
        </div>
      </nav>

      <div className="flex-1 max-w-3xl mx-auto px-4 py-8 w-full">
        {/* Header */}
        <div className="bg-white rounded-2xl p-6 sm:p-8 mb-4" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: '#E8F7FC' }}>
              <FileText className="w-6 h-6 text-[#00A3E0]" />
            </div>
            <div>
              <h1 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: '#1C1C1E' }}>{certType}</h1>
              <p className="text-sm font-mono" style={{ color: '#8E8E93', fontFamily: 'IBM Plex Mono, monospace' }}>{certId}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
            {address && (
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#00A3E0]" />
                <div>
                  <p className="text-[10px] font-semibold" style={{ color: '#8E8E93' }}>Address</p>
                  <p className="text-sm" style={{ color: '#1C1C1E' }}>{address}</p>
                </div>
              </div>
            )}
            {client && (
              <div className="flex items-start gap-3">
                <User className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#00A3E0]" />
                <div>
                  <p className="text-[10px] font-semibold" style={{ color: '#8E8E93' }}>Customer</p>
                  <p className="text-sm" style={{ color: '#1C1C1E' }}>{client}</p>
                </div>
              </div>
            )}
            {date && (
              <div className="flex items-start gap-3">
                <Calendar className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#00A3E0]" />
                <div>
                  <p className="text-[10px] font-semibold" style={{ color: '#8E8E93' }}>Date</p>
                  <p className="text-sm" style={{ color: '#1C1C1E' }}>{date}</p>
                </div>
              </div>
            )}
            {contractor && (
              <div className="flex items-start gap-3">
                <Shield className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#D4A537]" />
                <div>
                  <p className="text-[10px] font-semibold" style={{ color: '#8E8E93' }}>Issued By</p>
                  <p className="text-sm" style={{ color: '#1C1C1E' }}>{contractor}</p>
                </div>
              </div>
            )}
          </div>

          <div className="mt-6 pt-4 border-t border-[#E5E5EA]">
            <button onClick={handleDownload} data-testid="download-certificate-btn"
              className="flex items-center justify-center gap-2 w-full sm:w-auto bg-[#00A3E0] text-white font-semibold rounded-xl px-6 py-3 text-sm hover:bg-[#0082B3] transition-all">
              <Download className="w-4 h-4" /> Download PDF Certificate
            </button>
          </div>
        </div>

        {/* Certificate Details */}
        {cert.form_data && typeof cert.form_data === "object" && Object.keys(cert.form_data).length > 0 && (
          <div className="bg-white rounded-2xl p-6 sm:p-8" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
            <h2 className="text-base font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif', color: '#1C1C1E' }}>Certificate Details</h2>
            <div className="space-y-2">
              {Object.entries(cert.form_data).map(([key, value]) => {
                if (!value || typeof value === "object") return null;
                const label = key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
                return (
                  <div key={key} className="flex justify-between items-start gap-4 py-2 border-b border-[#F2F2F7] last:border-0">
                    <span className="text-xs font-semibold flex-shrink-0" style={{ color: '#8E8E93' }}>{label}</span>
                    <span className="text-sm text-right" style={{ color: '#1C1C1E' }}>{String(value)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-[#1C1C1E] py-6 mt-auto">
        <div className="text-center text-xs text-white/30">
          &copy; {new Date().getFullYear()} Self Certify UK. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
